"use client";

import React from "react";
import { useQuery } from "@tanstack/react-query";
import { learningApi } from "@/lib/api/services/learning.api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { GraduationCap, Plus, BookOpen, Clock } from "lucide-react";
import Link from "next/link";

interface Course {
  id: string;
  title: string;
  description: string;
  category: string;
  instructor: string;
  duration: string;
  createdAt: string;
}

export default function LearningPage() {
  const { data: courses, isLoading } = useQuery({
    queryKey: ["learning-courses"],
    queryFn: async () => {
      const data = await learningApi.listCourses();
      return ((data as unknown as { data?: Course[] }).data ?? []) as Course[];
    },
  });

  return (
    <div className="mx-auto max-w-3xl px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-xl font-semibold tracking-tight text-foreground">Learning</h1>
          <p className="mt-1 text-sm text-muted-foreground">Courses designed for HR professionals.</p>
        </div>
        <Button asChild size="sm">
          <Link href="/learning/courses">
            <Plus className="mr-2 h-4 w-4" /> Browse Courses
          </Link>
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="space-y-3 p-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded-full bg-muted/10 animate-pulse shrink-0" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 w-3/4 bg-muted/10 rounded animate-pulse" />
                    <div className="h-3 w-1/2 bg-muted/10 rounded animate-pulse" />
                  </div>
                </div>
              ))}
            </div>
          ) : !courses || courses.length === 0 ? (
            <div className="py-16 text-center">
              <GraduationCap className="mx-auto h-10 w-10 text-muted-foreground/30" />
              <h3 className="mt-4 font-heading text-base font-semibold text-foreground">No courses available</h3>
              <p className="mt-1 text-sm text-muted-foreground">Check back later for new courses.</p>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {courses.map((course) => (
                <Link key={course.id} href={`/learning/courses/${course.id}`}>
                  <Card className="group h-full hover:border-neutral-300 transition-all duration-150">
                    <CardContent className="p-5">
                      <div className="flex items-center gap-2 mb-3">
                        <Badge variant="secondary" size="sm">{course.category}</Badge>
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <Clock className="h-3 w-3" /> {course.duration}
                        </span>
                      </div>
                      <h3 className="text-sm font-heading font-semibold text-foreground group-hover:text-primary transition-colors line-clamp-2 mb-2">
                        {course.title}
                      </h3>
                      <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2">{course.description}</p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
